import { useState } from 'react';
import { X, Globe, AlertCircle } from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { CREATE_SITE } from '../../graphql/mutations';

const client = generateClient();

interface AddSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSiteModal({ isOpen, onClose, onSuccess }: AddSiteModalProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate URL
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setIsSubmitting(true);

    try {
      await client.graphql({
        query: CREATE_SITE,
        variables: {
          input: {
            name,
            url,
            description: description || undefined,
          },
        },
      });

      // Reset form and close
      setName('');
      setUrl('');
      setDescription('');
      onSuccess();
    } catch (err) {
      console.error('Error creating site:', err);
      setError('Failed to create site. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setUrl('');
      setDescription('');
      setError(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-primary/80"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-light">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-coral/10 rounded-full flex items-center justify-center">
              <Globe className="text-coral" size={20} />
            </div>
            <h2 className="text-heading-3 font-semibold">Add New Site</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-status-error/10 text-status-error rounded-card">
              <AlertCircle size={18} />
              <span className="text-body-sm">{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-body-sm font-medium mb-1.5">
              Site Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="My Website"
              className="input"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="url" className="block text-body-sm font-medium mb-1.5">
              URL *
            </label>
            <input
              id="url"
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="input"
              required
              disabled={isSubmitting}
            />
            <p className="text-caption text-gray-medium mt-1">
              Enter the full URL including https://
            </p>
          </div>

          <div>
            <label htmlFor="description" className="block text-body-sm font-medium mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the website (optional)"
              className="input min-h-[80px] resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name || !url}
              className="btn-primary flex-1"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Adding...
                </span>
              ) : (
                'Add Site'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
